import { Link } from 'react-router-dom';
import { 
  Building2, 
  Briefcase, 
  FlaskConical, 
  GraduationCap,
  CheckCircle2,
  ChevronRight
} from 'lucide-react';

export const About = () => {
  const competencies = [
    {
      icon: Building2,
      title: 'Construction & Civil Engineering',
      description: 'Executing large-scale construction and civil engineering projects for government and private sector clients.',
    },
    {
      icon: Briefcase,
      title: 'Engineering Consulting',
      description: 'Expert consulting services for infrastructure projects, from planning to execution.',
    },
    {
      icon: Building2,
      title: 'Turnkey Project Execution',
      description: 'End-to-end turnkey solutions for Government and Corporate projects across India.',
    },
    {
      icon: FlaskConical,
      title: 'Trading & Import of Chemicals',
      description: 'Authorised dealing and import of specialised industrial chemicals including PET and Resin products.',
    },
    {
      icon: GraduationCap,
      title: 'Vocational Training & Manpower',
      description: 'Skill development, vocational training, and manpower consulting solutions.',
    },
  ];

  const milestones = [
    { year: '1983', event: 'Company Incorporated', description: 'Founded in Udaipur, Rajasthan with a vision to build India\'s infrastructure' },
    { year: '1985', event: 'Cement Industry Solution', description: 'Specialized construction of silos, preheaters, and CCR buildings for cement plants' },
    { year: '1990s', event: 'Government Projects', description: 'Expanded into government infrastructure and turnkey projects' },
    { year: '2000s', event: 'Pan-India Expansion', description: 'Established presence across multiple states in India' },
    { year: '2011', event: 'Housing Construction', description: 'Entered housing and apartment construction sector' },
    { year: '2016', event: 'Chemical Business', description: 'Diversified into trading and import of specialised industrial chemicals' },
    { year: '2022', event: 'Vocational & Skilling', description: 'Entered vocational training and manpower consulting sector' },
  ];

  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section 
        data-testid="about-hero"
        className="relative py-20 md:py-32 bg-[#1A2B4A]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <p className="overline mb-4">About Us</p>
            <h1 
              data-testid="about-title"
              className="text-4xl sm:text-5xl font-bold text-white mb-6"
            >
              Four Decades of<br />
              <span className="text-[#C9A84C]">Engineering Excellence</span>
            </h1>
            <p className="text-lg text-[#F5F5F0]/90">
              Since 1983, Kamal Engineers and Contractors has been at the forefront of 
              India's infrastructure development, delivering quality and reliability.
            </p>
          </div>
        </div>
      </section>

      {/* Company Story Section */}
      <section 
        data-testid="company-story"
        className="py-20 md:py-32 bg-[#F5F5F0]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="overline mb-4">Our Story</p>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1A2B4A] mb-6">
                Building Trust Since 1983
              </h2>
              <div className="space-y-4 text-[#4A5568] leading-relaxed">
                <p>
                  <strong className="text-[#1A2B4A]">Kamal Engineers and Contractors Pvt Ltd</strong> was 
                  incorporated in 1983 with a vision to contribute to India's growing infrastructure needs. 
                  With over <strong className="text-[#1A2B4A]">40 years of experience</strong>, we have 
                  established ourselves as a trusted partner for government bodies and corporate clients alike.
                </p>
                <p>
                  Our journey began with civil construction projects in Rajasthan and has since expanded to 
                  cover the entire nation. Today, we offer a comprehensive range of services including 
                  construction, engineering consulting, turnkey project execution, chemical trading, and 
                  vocational training.
                </p>
                <p>
                  We take pride in our commitment to quality, timely delivery, and maintaining the highest 
                  standards of professional integrity. Our diverse portfolio of successfully completed 
                  projects stands testimony to our capabilities and dedication.
                </p>
              </div>
              <Link 
                to="/services" 
                className="inline-flex items-center gap-2 mt-8 btn-gold"
              >
                View Our Services
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1664972988916-da679f400f2b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBlbmdpbmVlcnMlMjBzaXRlJTIwYmx1ZXByaW50c3xlbnwwfHx8fDE3NzU2NDU2ODh8MA&ixlib=rb-4.1.0&q=85"
                alt="Professional engineers reviewing blueprints"
                className="w-full rounded-sm shadow-xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#C9A84C] text-[#1A2B4A] p-6 rounded-sm shadow-lg">
                <div className="text-4xl font-bold">40+</div>
                <div className="text-sm font-semibold">Years of Excellence</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section 
        data-testid="timeline-section"
        className="py-20 bg-white"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="overline mb-4">Our Journey</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1A2B4A]">
              Key Milestones
            </h2>
          </div>
          
          {/* Timeline for Desktop - Two Rows */}
          <div className="hidden lg:block">
            <div className="relative space-y-16">
              {/* First Row - 1983 to 2000s */}
              <div className="relative">
                <div className="absolute top-8 left-0 right-0 h-1 bg-gradient-to-r from-[#1A2B4A] via-[#C9A84C] to-[#1A2B4A]" />
                <div className="flex justify-between">
                  {milestones.slice(0, 4).map((milestone, index) => (
                    <div 
                      key={index}
                      data-testid={`milestone-${index}`}
                      className="relative flex flex-col items-center text-center w-1/4 px-3"
                    >
                      <div className="w-16 h-16 rounded-full bg-[#1A2B4A] flex items-center justify-center mb-6 z-10 border-4 border-[#C9A84C] shadow-lg">
                        <span className="text-white font-bold text-sm">{milestone.year}</span>
                      </div>
                      <div className="bg-[#F5F5F0] p-5 rounded-sm border-l-4 border-[#C9A84C] hover:shadow-lg transition-shadow duration-300 w-full">
                        <h4 className="text-[#1A2B4A] font-bold text-lg mb-2">{milestone.event}</h4>
                        <p className="text-[#4A5568] text-sm leading-relaxed">{milestone.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Second Row - 2011 to 2022 */}
              <div className="relative">
                <div className="absolute top-8 left-[12.5%] right-[12.5%] h-1 bg-gradient-to-r from-[#1A2B4A] via-[#C9A84C] to-[#1A2B4A]" />
                <div className="flex justify-center gap-8">
                  {milestones.slice(4).map((milestone, index) => (
                    <div 
                      key={index + 4}
                      data-testid={`milestone-${index + 4}`}
                      className="relative flex flex-col items-center text-center w-1/4 px-3"
                    >
                      <div className="w-16 h-16 rounded-full bg-[#1A2B4A] flex items-center justify-center mb-6 z-10 border-4 border-[#C9A84C] shadow-lg">
                        <span className="text-white font-bold text-sm">{milestone.year}</span>
                      </div>
                      <div className="bg-[#F5F5F0] p-5 rounded-sm border-l-4 border-[#C9A84C] hover:shadow-lg transition-shadow duration-300 w-full">
                        <h4 className="text-[#1A2B4A] font-bold text-lg mb-2">{milestone.event}</h4>
                        <p className="text-[#4A5568] text-sm leading-relaxed">{milestone.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Vertical Timeline for Mobile/Tablet */}
          <div className="lg:hidden">
            <div className="relative">
              {/* Vertical Line */}
              <div className="absolute left-6 top-0 bottom-0 w-1 bg-gradient-to-b from-[#1A2B4A] via-[#C9A84C] to-[#1A2B4A]" />
              
              <div className="space-y-8">
                {milestones.map((milestone, index) => (
                  <div 
                    key={index}
                    data-testid={`milestone-mobile-${index}`}
                    className="relative flex items-start gap-6 pl-2"
                  >
                    {/* Year Circle */}
                    <div className="w-12 h-12 rounded-full bg-[#1A2B4A] flex items-center justify-center flex-shrink-0 z-10 border-3 border-[#C9A84C] shadow-md">
                      <span className="text-white font-bold text-xs">{milestone.year}</span>
                    </div>
                    
                    {/* Content Card */}
                    <div className="flex-1 bg-[#F5F5F0] p-5 rounded-sm border-l-4 border-[#C9A84C] hover:shadow-lg transition-shadow duration-300">
                      <h4 className="text-[#1A2B4A] font-bold text-lg mb-2">{milestone.event}</h4>
                      <p className="text-[#4A5568] text-sm leading-relaxed">{milestone.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Competencies Section */}
      <section 
        data-testid="competencies-section"
        className="py-20 md:py-32 bg-[#F5F5F0]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="overline mb-4">What We Do</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1A2B4A] mb-4">
              Core Competencies
            </h2>
            <p className="text-[#4A5568] max-w-2xl mx-auto">
              Our diverse capabilities enable us to deliver comprehensive solutions 
              across multiple sectors.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {competencies.map((item, index) => (
              <div
                key={index}
                data-testid={`competency-card-${index}`}
                className="bg-white p-6 border border-[#4A5568]/15 rounded-sm hover:border-[#C9A84C] transition-colors duration-300"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded bg-[#1A2B4A] flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-6 h-6 text-[#C9A84C]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#1A2B4A] mb-2">{item.title}</h3>
                    <p className="text-[#4A5568] text-sm leading-relaxed">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Legal Information Section */}
      <section 
        data-testid="legal-section"
        className="py-20 bg-[#1A2B4A]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <p className="overline mb-4">Official Details</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white">
              Company Information
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div 
              data-testid="legal-cin"
              className="bg-white/5 p-8 rounded-sm border border-white/10"
            >
              <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-wider mb-2">
                Corporate Identity Number (CIN)
              </p>
              <p className="text-white text-xl font-mono">U45201RJ1983PTC002692</p>
            </div>
            <div 
              data-testid="legal-address"
              className="bg-white/5 p-8 rounded-sm border border-white/10"
            >
              <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-wider mb-2">
                Registered Office
              </p>
              <p className="text-white">
                Shree Niketan, 380, Ashok Nagar,<br />
                Udaipur – 313001, Rajasthan, India
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        data-testid="about-cta"
        className="py-20 bg-[#F5F5F0]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#1A2B4A] mb-4">
            Ready to Work Together?
          </h2>
          <p className="text-[#4A5568] mb-8 max-w-2xl mx-auto">
            Connect with our team to discuss how we can support your infrastructure 
            and engineering projects.
          </p>
          <Link 
            to="/contact" 
            data-testid="about-contact-cta"
            className="btn-gold inline-flex items-center gap-2"
          >
            Get in Touch
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
